import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Sparkles, ClipboardList, Save } from 'lucide-react';
import { toast } from 'sonner';
import { needsAnalysisAPI } from '@/lib/api';

const EDITABLE = ['current_doctors', 'current_medications'];

/**
 * Read-only Needs Analysis on a Client, EXCEPT Doctors & Medications which
 * stay editable (the client's situation changes over time). Cross-sell is
 * rendered as a plain text note.
 */
export default function ClientNeedsAnalysisCard({ clientId, needsAnalysis }) {
  const na = needsAnalysis || {};
  const answers = na.answers || [];
  const lang = String(na.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en';
  const findAns = (id) => (answers.find((a) => a.id === id) || {}).answer || '';

  const [doctors, setDoctors] = useState(findAns('current_doctors'));
  const [meds, setMeds] = useState(findAns('current_medications'));
  const [saving, setSaving] = useState(false);

  const t = (en, es) => (lang === 'es' ? es : en);
  const readOnly = answers.filter((a) => !EDITABLE.includes(a.id) && (a.answer || '').trim());
  const cross = na.cross_sell || [];

  const save = async () => {
    setSaving(true);
    try {
      await needsAnalysisAPI.saveClientDoctorsMeds(clientId, {
        current_doctors: doctors, current_medications: meds,
      });
      toast.success(t('Doctors & medications saved', 'Médicos y medicamentos guardados'));
    } catch {
      toast.error(t('Could not save', 'No se pudo guardar'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="client-needs-analysis-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <ClipboardList size={16} /> {t('Needs Analysis', 'Análisis de Necesidades')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">{t('Current doctors', 'Médicos actuales')}</Label>
            <Textarea rows={2} value={doctors} onChange={(e) => setDoctors(e.target.value)} data-testid="client-na-doctors" />
          </div>
          <div>
            <Label className="text-xs">{t('Current medications', 'Medicamentos actuales')}</Label>
            <Textarea rows={2} value={meds} onChange={(e) => setMeds(e.target.value)} data-testid="client-na-meds" />
          </div>
        </div>
        <Button size="sm" onClick={save} disabled={saving} data-testid="client-na-save">
          <Save size={14} className="mr-1" /> {t('Save', 'Guardar')}
        </Button>

        {readOnly.length > 0 && (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 pt-2 border-t border-slate-100">
            {readOnly.map((a) => (
              <div key={a.id} className="text-sm">
                <dt className="text-xs text-slate-400">{a.question}</dt>
                <dd className="text-slate-800 whitespace-pre-wrap">{a.answer}</dd>
              </div>
            ))}
          </dl>
        )}

        {na.ai_summary && (
          <div className="rounded-md border border-indigo-200 bg-indigo-50 p-3" data-testid="client-na-ai">
            <p className="text-xs font-semibold text-indigo-900 mb-1 flex items-center gap-1">
              <Sparkles size={12} /> {t('AI Recommendation', 'Recomendación AI')}
            </p>
            <p className="text-sm text-indigo-900 whitespace-pre-wrap">{na.ai_summary}</p>
          </div>
        )}

        {cross.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="client-na-cross-note">
            <p className="text-xs font-semibold text-amber-900 mb-0.5">
              {t('Note — Cross-sell opportunities', 'Nota — Oportunidades de venta cruzada')}
            </p>
            <p className="text-sm text-amber-900">{cross.join(', ')}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
